import { LGraphNode, LiteGraph } from "litegraph.js";

export class TextInputNode extends LGraphNode {
    constructor() {
        super();
        this.title = "Text Input";

        this.properties = {
            text: this.getUrlParam("text") || "Futures"
        };

        this.addOutput("Text", "string");
        this.mode = LiteGraph.ALWAYS;
        this.size = [200, 56];
        this.color = "#2a2a2a";
        this.bgcolor = "#333333";

        this._editing = false;
        this._inputEl = null;
    }

    getUrlParam(param) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }

    updateUrlParam() {}

    onExecute() {
        this.setOutputData(0, this.properties.text);
    }

    onDrawForeground(ctx) {
        if (this._editing) return;

        const pad = 10;
        const y = 30;
        const w = this.size[0] - pad * 2;

        // Text background
        ctx.fillStyle = "rgba(0,0,0,0.25)";
        ctx.fillRect(pad, y - 12, w, 20);

        // Text content
        ctx.fillStyle = "#fff";
        ctx.font = "12px -apple-system, sans-serif";
        let text = this.properties.text;
        if (text.length > 24) text = text.substring(0, 24) + '\u2026';
        ctx.fillText(text, pad + 6, y + 2);

        // Edit hint
        ctx.fillStyle = "rgba(255,255,255,0.25)";
        ctx.font = "9px -apple-system, sans-serif";
        ctx.textAlign = "right";
        ctx.fillText("click to edit", pad + w - 2, y + 2);
        ctx.textAlign = "left";
    }

    onMouseDown(event, pos) {
        if (pos[1] > 16 && pos[1] < 50) {
            this._openEditor();
            return true;
        }
        return false;
    }

    _openEditor() {
        if (this._editing) return;

        // Find the LGraphCanvas instance to convert node coords → screen coords
        const lgCanvas = this.graph?.list_of_graphcanvas?.[0];
        if (!lgCanvas || !lgCanvas.canvas) return;

        const canvasRect = lgCanvas.canvas.getBoundingClientRect();
        const offset = lgCanvas.ds.offset;
        const scale = lgCanvas.ds.scale;
        const screenX = canvasRect.left + (this.pos[0] + 10 + offset[0]) * scale;
        const screenY = canvasRect.top + (this.pos[1] + 18 + offset[1]) * scale;

        this._editing = true;

        const input = document.createElement("textarea");
        input.value = this.properties.text;
        input.rows = 3;
        this._inputEl = input;

        // Style inline so it doesn't depend on external CSS
        Object.assign(input.style, {
            position: "fixed",
            left: `${screenX}px`,
            top: `${screenY}px`,
            width: `${Math.max(200, this.size[0] * scale - 20)}px`,
            minHeight: "28px",
            zIndex: "10000",
            background: "#1a1a1a",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "4px",
            color: "#fff",
            fontSize: "13px",
            fontFamily: "-apple-system, sans-serif",
            padding: "6px 8px",
            outline: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            resize: "both",
            lineHeight: "1.4",
        });

        document.body.appendChild(input);

        // Select all text for easy replacement
        requestAnimationFrame(() => {
            input.focus();
            input.select();
        });

        const close = () => {
            if (!this._editing) return;
            this._editing = false;
            this._inputEl = null;
            this.properties.text = input.value;
            this.updateUrlParam("text", input.value);
            this.setDirtyCanvas(true);
            if (input.parentNode) input.parentNode.removeChild(input);
        };

        input.addEventListener("input", () => {
            this.properties.text = input.value;
            this.updateUrlParam("text", input.value);
            this.setDirtyCanvas(true);
        });

        input.addEventListener("keydown", (e) => {
            e.stopPropagation();
            if (e.key === "Enter" && (e.shiftKey || e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                close();
            }
            if (e.key === "Escape") {
                e.preventDefault();
                close();
            }
        });

        // Prevent LiteGraph from stealing keystrokes
        input.addEventListener("keyup", (e) => e.stopPropagation());
        input.addEventListener("keypress", (e) => e.stopPropagation());

        input.addEventListener("blur", () => {
            // Small delay so clicking elsewhere doesn't race
            setTimeout(close, 150);
        });
    }

    onRemoved() {
        // Clean up if node is deleted while editing
        if (this._inputEl && this._inputEl.parentNode) {
            this._inputEl.parentNode.removeChild(this._inputEl);
        }
        this._editing = false;
    }
}

LiteGraph.registerNodeType("custom/text_input", TextInputNode);
